/* File: src/components/account/AccountEmailSection.js */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faExclamationTriangle, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

// REMOVE direct imports of UI components from ../ui/

const AccountEmailSection = ({
    authUser,
    newEmail,
    setNewEmail,
    currentPasswordForEmail,
    setCurrentPasswordForEmail,
    emailUpdateLoading,
    emailUpdateError,
    emailUpdateSuccess,
    handleEmailUpdate,
    isSendingVerification,
    verificationMessage,
    verificationMessageType,
    handleSendVerificationEmail,
    // UI Components passed as props
    InputFieldComponent,
    ButtonComponent,
    AlertComponent,
    StatusBadgeComponent,
}) => {
    // Use the passed-in components. It's common to rename them locally for convenience.
    const InputField = InputFieldComponent;
    const Button = ButtonComponent;
    const Alert = AlertComponent;
    const StatusBadge = StatusBadgeComponent;

    if (!authUser) {
        return null;
    }

    // It's good practice to add a check in case the components aren't passed
    if (!InputField || !Button || !Alert || !StatusBadge) {
        console.error("AccountEmailSection: Required UI component props were not provided.");
        return <div className="text-red-500 p-4">Error: UI components for this section are missing.</div>;
    }

    return (
        <>
            {/* Current Email Display & Verification Status */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                    <p className="text-sm text-gray-600">Current Email:</p>
                    <p className="font-medium break-all text-gray-800">{authUser.email}</p>
                </div>
                <div className="sm:text-right">
                    {authUser.emailVerified ? (
                        <StatusBadge type="success" text="Verified" />
                    ) : (
                        <StatusBadge type="warning" text="Not Verified" />
                    )}
                </div>
            </div>

            {/* EMAIL VERIFICATION CALLOUT (only if not verified) */}
            {!authUser.emailVerified && (
                <div className="mb-6 p-4 border border-yellow-200 bg-yellow-50 rounded-md">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 h-4 w-4" />
                        Verify Your Email Address
                    </h4>
                    <p className="text-sm text-yellow-700 mb-3">
                        Verifying your email helps secure your account and ensures you receive important updates.
                    </p>
                    <Alert type={verificationMessageType} message={verificationMessage} />
                    {(!verificationMessage || verificationMessageType === 'error') && (
                        <Button
                            onClick={handleSendVerificationEmail}
                            text="Send Verification Email"
                            color="yellow"
                            icon={faPaperPlane}
                            isLoading={isSendingVerification}
                            loadingText="Sending..."
                            disabled={isSendingVerification}
                        />
                    )}
                </div>
            )}

            <Alert
                type="success"
                message={emailUpdateSuccess ? "Email updated successfully! Please check your new email for verification." : null}
            />
            <Alert
                type="error"
                message={emailUpdateError && !emailUpdateError.toLowerCase().includes("email") && !emailUpdateError.toLowerCase().includes("password") ? emailUpdateError : null}
            />

            <form onSubmit={handleEmailUpdate} noValidate className="space-y-4">
                <InputField
                    id="newEmail"
                    label="New Email Address"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    placeholder="Enter new email address"
                    disabled={emailUpdateLoading}
                    error={emailUpdateError && emailUpdateError.toLowerCase().includes("email") ? emailUpdateError : null}
                />
                <InputField
                    id="currentPasswordForEmail"
                    label="Current Password (for verification)"
                    type="password"
                    value={currentPasswordForEmail}
                    onChange={(e) => setCurrentPasswordForEmail(e.target.value)}
                    required
                    placeholder="Enter your current password"
                    disabled={emailUpdateLoading}
                    error={emailUpdateError && emailUpdateError.toLowerCase().includes("password") ? emailUpdateError : null}
                />

                <div className="flex justify-end">
                    <Button
                        type="submit"
                        text="Update Email"
                        color="indigo"
                        icon={faEnvelope}
                        isLoading={emailUpdateLoading}
                        loadingText="Updating Email..."
                        disabled={emailUpdateLoading || !newEmail || !currentPasswordForEmail}
                        fullWidth={false}
                    />
                </div>
            </form>
        </>
    );
};

export default AccountEmailSection;